// src/pages/FateFriday.js
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Seo from "../components/seo";
import cardData from "../components/card-data";
import { cardPath } from "../utils/cardRoutes";
import { currentLanguage, withLanguage } from "../utils/locale";
import {
  localizedPath,
  translateCardAlt,
  translateCardName,
  translateKeywordPhrase,
  translateSuitName,
} from "../utils/i18n";

/* =========================================================
   THEME + API
========================================================= */

const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE) ||
  "https://tarotfate-api.tarotfate-com.workers.dev";
const WRAP = "px-[1vw]";
const MAXW = "max-w-10xl mx-auto";
const FONT = "font-[Cormorant_Garamond]";
const TEXT = "text-white/90";
const BORDER_SOFT = "border-[#005C83]/12";
const PANEL_BASE =
  "relative overflow-hidden rounded-md " +
  BORDER_SOFT +
  " shadow-[0_2px_4px_rgba(0,0,0,0.35)]";

/* Canvas background */
const CANVAS_BG = `
  radial-gradient(1200px 900px at 50% -12%, rgba(3,121,155,0.32), rgba(3,121,155,0) 40%),
  linear-gradient(180deg, #014C75 0%, #005C83 22%, #016280ff 38%, #0C4B84 72%, #014C75 90%, #821E3C 100%)
`;

/* Silk panel + accents */
const SILK_TURQ =
  "bg-[linear-gradient(135deg,rgba(12,63,107,0.40),rgba(20,33,61,0.88))] " +
  "before:absolute before:inset-0 before:bg-[radial-gradient(80%_60%_at_80%_0%,rgba(59,198,214,0.25),transparent_100%)] before:content-['']";

const RIBBON_GRAD =
  "bg-[linear-gradient(90deg,rgba(203,183,234,0.12),rgba(59,198,214,0.18),rgba(203,183,234,0.12))]";

/* Suit accent chips */
const SUIT_STYLE = {
  "Major Arcana":
    "bg-[linear-gradient(135deg,rgba(203,183,234,0.25),rgba(255,255,255,0.06))] border-white/25",
  Wands:
    "bg-[linear-gradient(135deg,rgba(255,140,66,0.28),rgba(255,255,255,0.06))] border-white/25",
  Cups:
    "bg-[linear-gradient(135deg,rgba(112,193,255,0.28),rgba(255,255,255,0.06))] border-white/25",
  Swords:
    "bg-[linear-gradient(135deg,rgba(186,221,255,0.28),rgba(255,255,255,0.06))] border-white/25",
  Pentacles:
    "bg-[linear-gradient(135deg,rgba(185,255,120,0.28),rgba(255,255,255,0.06))] border-white/25",
};

/* =========================================================
   SEO (mirror About.js patterns)
========================================================= */
const siteUrl = "https://tarotfate.com";
const pageUrl = `${siteUrl}/fate-friday`;
const pageUrlEs = `${siteUrl}/es/fate-friday`;
const ogImage = `${siteUrl}/icons/og-cover.jpg`;
const orgLogo = `${siteUrl}/icons/logo.webp`;

const metaDescription =
  "Fate Friday: your weekly tarot focus. Every Friday a featured card appears with image, meaning and a practical 500-700 word guide for action. Explore recent weeks and learn the method.";

const pageKeywords = `
  Fate Friday, weekly tarot, card of the week, Friday tarot, tarot focus,
  major arcana, minor arcana, wands, cups, swords, pentacles, tarot meanings,
  upright meanings, reversed meanings, learn tarot, tarot study, tarot guide,
  spiritual growth, personal development, reflection, mindfulness
`.replace(/\s+/g, " ").trim();

/* =========================================================
   Helpers
========================================================= */
const wc = (s) => (s || "").trim().split(/\s+/).filter(Boolean).length;

function suitSlug(suit) {
  switch (suit) {
    case "Major Arcana":
      return "major-arcana";
    case "Wands":
      return "wands";
    case "Cups":
      return "cups";
    case "Swords":
      return "swords";
    case "Pentacles":
      return "pentacles";
    default:
      return "tarot-cards";
  }
}

/* Deterministic weekly selection (UTC Friday 09:00) */
function fridayKeyUTC(date = new Date(), hourUTC = 9) {
  const now = date;
  const dow = now.getUTCDay(); // 0 Sun .. 5 Fri .. 6 Sat
  const daysSinceFriday = (7 + dow - 5) % 7;
  const lastFriday = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  );
  lastFriday.setUTCDate(lastFriday.getUTCDate() - daysSinceFriday);
  lastFriday.setUTCHours(hourUTC, 0, 0, 0);
  if (now < lastFriday) lastFriday.setUTCDate(lastFriday.getUTCDate() - 7);
  const epoch = new Date(Date.UTC(2024, 0, 5, hourUTC, 0, 0, 0));
  const weeksSince = Math.floor((lastFriday - epoch) / 604800000);
  return { anchor: lastFriday, weeksSince };
}
function indexForWeek(total, weeksSince) {
  const seed = (0x9e3779b1 ^ (weeksSince * 131) ^ (weeksSince >>> 1)) >>> 0;
  return seed % total;
}
function getISOWeekFromUTC(d) {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
  return weekNo;
}
function formatFriday(dt, withWeek = false, lang = currentLanguage()) {
  const isSpanish = lang === "es";
  const fmt = new Intl.DateTimeFormat(isSpanish ? "es" : undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
  const label = fmt.format(dt);
  if (!withWeek) return label;
  const weekNo = getISOWeekFromUTC(dt);
  return `${label} - ${isSpanish ? "Semana" : "Week"} ${weekNo}`;
}
function pickThisWeek(items) {
  const { anchor, weeksSince } = fridayKeyUTC();
  const idx = indexForWeek(items.length, weeksSince);
  return {
    card: items[idx],
    anchor,
    weeksSince,
    key: `${anchor.toISOString().slice(0, 10)}-Fri`,
  };
}
function getPastWeeks(items, count = 8) {
  const out = [];
  const now = new Date();
  for (let i = 1; i <= count; i++) {
    const past = new Date(now);
    past.setUTCDate(past.getUTCDate() - i * 7);
    const { anchor, weeksSince } = fridayKeyUTC(past);
    const idx = indexForWeek(items.length, weeksSince);
    out.push({
      card: items[idx],
      anchor,
      weeksSince,
      key: `${anchor.toISOString().slice(0, 10)}-Fri`,
    });
  }
  return out;
}

/* =========================================================
   JSON-LD (About.js-style structure)
========================================================= */
function schemaGraph(thisWeek, pastWeeks, isSpanish = false) {
  const activePageUrl = isSpanish ? pageUrlEs : pageUrl;
  const activeLang = isSpanish ? "es" : "en";
  const activeTitle = isSpanish ? "Viernes del destino | Guia semanal de tarot" : "Fate Friday | Weekly Tarot Feature";
  const activeDescription = isSpanish
    ? "Una carta semanal de tarot con significado, imagen y una accion concreta para llevar la lectura a tu vida."
    : metaDescription;
  const allShown = [thisWeek, ...pastWeeks];
  const itemListElement = allShown.map((w, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: `${isSpanish ? translateCardName(w.card.name) : w.card.name} - ${formatFriday(w.anchor, false, activeLang)}`,
    url: `${siteUrl}${isSpanish ? localizedPath(cardPath(w.card)) : cardPath(w.card)}`,
  }));

  const faq = isSpanish
    ? [
        {
          q: "Que es Viernes del destino",
          a: "Es una carta destacada de tarot que se publica cada viernes. La seleccion cambia una vez por semana y todos los visitantes ven la misma guia.",
        },
        {
          q: "Cuando cambia la carta",
          a: "La carta cambia los viernes a las 09:00 UTC y permanece igual hasta el viernes siguiente.",
        },
        {
          q: "Como se elige la carta",
          a: "La pagina usa un metodo estable basado en la cantidad de semanas desde un viernes fijo. No depende de una base de datos.",
        },
      ]
    : [
        {
          q: "What is Fate Friday",
          a: "It is a weekly featured tarot card published every Friday. The selection is deterministic and changes once per week at a fixed time.",
        },
        {
          q: "When does the card change",
          a: "The card changes on Friday at 09:00 Coordinated Universal Time. All visitors see the same feature for the entire week.",
        },
        {
          q: "How is the card chosen",
          a: "A simple seeded method uses the number of weeks since a fixed Friday in the past. The result is stable and does not require a database.",
        },
      ];

  const faqEntities = faq.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  }));

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}#org`,
        name: "TarotFate",
        url: siteUrl,
        logo: {
          "@type": "ImageObject",
          url: orgLogo,
          width: 512,
          height: 512,
        },
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}#website`,
        url: siteUrl,
        name: "TarotFate",
        publisher: { "@id": `${siteUrl}#org` },
        inLanguage: activeLang,
      },
      {
        "@type": "WebPage",
        "@id": `${activePageUrl}#webpage`,
        url: activePageUrl,
        name: activeTitle,
        description: activeDescription,
        breadcrumb: { "@id": `${activePageUrl}#breadcrumbs` },
        isPartOf: { "@id": `${siteUrl}#website` },
        primaryImageOfPage: { "@id": `${activePageUrl}#image` },
        inLanguage: activeLang,
      },
      {
        "@type": "ImageObject",
        "@id": `${activePageUrl}#image`,
        url: ogImage,
        width: 1200,
        height: 630,
        caption: "TarotFate cover",
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${activePageUrl}#breadcrumbs`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: isSpanish ? "Inicio" : "Home", item: isSpanish ? `${siteUrl}/es` : siteUrl },
          { "@type": "ListItem", position: 2, name: isSpanish ? "Viernes del destino" : "Fate Friday", item: activePageUrl },
        ],
      },
      { "@type": "ItemList", "@id": `${activePageUrl}#weeks`, name: isSpanish ? "Semanas de Viernes del destino" : "Fate Friday Weeks", itemListElement },
      { "@type": "FAQPage", "@id": `${activePageUrl}#faq`, mainEntity: faqEntities },
    ],
  };
}

const ENGLISH_LEAK_RE = /\b(Fate Friday|Weekly Tarot|Published|Open full meaning|Explore this suit|Please wait|Preparing your guide|Use this card|upright|reversed|meaning|action|Friday|week|card)\b/i;

function spanishFateFridayNote(card) {
  const name = translateCardName(card?.name);
  const suit = translateSuitName(card?.suit);
  const theme = translateKeywordPhrase(card?.subtitle || "").toLowerCase();

  return [
    `Esta semana, ${name} abre una lectura centrada en ${theme || "presencia y claridad interior"}. No llega para darte una orden rigida, sino para ayudarte a mirar donde estas invirtiendo energia, esperanza, tiempo o emocion. ${suit} marca el tono de la experiencia y te invita a distinguir entre lo que se siente vivo y lo que ya solo permanece por costumbre.`,
    "Mira la carta como si fuera un espejo suave. Que parte de tu vida pide paciencia. Que parte necesita revision. Que deseo merece cuidado, y cual se esta sosteniendo solo por miedo a perder lo ya invertido. La guia de esta semana no consiste en correr hacia una respuesta inmediata. Consiste en evaluar con honestidad, sin castigarte por el camino recorrido.",
    "Al comienzo de la semana, baja el ritmo lo suficiente para notar el patron principal. Puede ser una conversacion que sigues aplazando, una tarea que repites sin revisar si todavia tiene sentido o una emocion que aparece cada vez que intentas avanzar. No necesitas resolver todo al mismo tiempo. El primer paso es nombrar con precision que parte de tu vida pide mas atencion. Escribe una frase simple: esto me pesa, esto necesita orden, esto ya no puede seguir igual.",
    "Para hoy, elige una accion pequena que te devuelva estabilidad. Puede ser responder un mensaje pendiente, ordenar un espacio, pedir una aclaracion, revisar una fecha importante o tomar distancia de una situacion que te deja confundida. La accion debe ser concreta y medible. Si no puedes completarla en menos de treinta minutos, dividela en algo mas pequeno. La claridad crece cuando tu cuerpo ve que puedes hacer una cosa real, no cuando tu mente intenta controlar todo el resultado.",
    "A mitad de semana, revisa lo que aprendiste de esa primera accion. Preguntate que cambio, que resistencia aparecio y que informacion nueva tienes ahora. Si nada cambio por fuera, mira si algo cambio por dentro. A veces el avance es reconocer que ya no quieres seguir negociando con lo mismo. Otras veces es descubrir que una conversacion que temias puede hacerse con mas calma de lo que imaginabas. La reflexion util no busca culpas. Busca patrones.",
    "Hacia el final de la semana, elige un compromiso sencillo. No tiene que ser una gran decision. Puede ser sostener un limite, pedir ayuda, descansar antes de responder, terminar una tarea incompleta o dejar de alimentar una fantasia que no esta apoyada por hechos. Si el tema toca amor, trabajo o familia, evita decidir desde la urgencia. Escribe primero lo que necesitas, lo que puedes ofrecer y lo que ya no puedes cargar sola. Esa lista puede mostrarte el siguiente paso con mas limpieza.",
    "La pregunta para cerrar es sencilla: que necesita mi energia para convertirse en algo real, cuidado y sostenible. Esta semana te pide mirar con madurez lo que has estado sintiendo y convertirlo en una accion clara. La intuicion ayuda, pero la practica sostiene. Cuando cierres la semana, vuelve a tres preguntas: que aprendi, que puedo soltar y que accion pequena me acerca a una vida mas honesta.",
  ].join("\n\n");
}

function fateFridayCopy(isSpanish, card) {
  const cardName = isSpanish ? translateCardName(card?.name) : card?.name;
  const cardSubtitle = isSpanish ? translateKeywordPhrase(card?.subtitle || "") : card?.subtitle;
  return isSpanish
    ? {
        title: "Viernes del destino",
        seoTitle: "Viernes del destino | Guia semanal de tarot - TarotFate",
        description:
          "Una carta semanal de tarot con significado, imagen y una accion concreta para llevar la lectura a tu vida.",
        intro: "Enfoque semanal de tarot: carta, significado y una accion posible.",
        published: "Publicado",
        cardName,
        cardSubtitle,
        cardIntro:
          "Usa esta carta como guia de la semana. Lee su significado al derecho e invertido y convierte un mensaje en una accion pequena que puedas completar.",
        openMeaning: "Abrir significado completo",
        exploreSuit: "Explorar este palo",
        guideTitle: "Guia de la semana",
        loading: "Espera un momento. Preparando tu guia...",
        error:
          "No se pudo cargar la guia de esta semana. La carta destacada sigue disponible.",
        recentTitle: "* Semanas recientes",
        recentIntro:
          "Revisa las ultimas selecciones y observa los patrones que se repiten. Usa las imagenes para sostener una practica constante.",
        methodTitle: "* Como se elige la carta",
        methodText:
          "La seleccion semanal usa la cantidad de semanas desde un viernes fijo como semilla. Este metodo es estable y no necesita base de datos ni tarea del servidor. La carta cambia los viernes a las nueve de la manana UTC y permanece igual hasta el viernes siguiente.",
        learnBasics: "Aprender tarot basico",
        browseCards: "Ver todas las cartas",
      }
    : {
        title: "Fate Friday",
        seoTitle: "Fate Friday | Weekly Tarot Feature - TarotFate",
        description: metaDescription,
        intro: "Weekly Tarot Focus: tarot card, meaning and one doable action.",
        published: "Published",
        cardName,
        cardSubtitle,
        cardIntro:
          "Use this card as a guide for the week. Read the page for upright and reversed meanings and translate one message into a small action you will complete.",
        openMeaning: "Open full meaning",
        exploreSuit: "Explore this suit",
        guideTitle: "Fate Friday",
        loading: "Please wait a moment. Preparing your guide...",
        error:
          "Could not load this week's Fate Friday guide. The card feature still works without it.",
        recentTitle: "* Recent Weeks",
        recentIntro:
          "Review the last selections and notice patterns that repeat. Use the images to anchor steady practice.",
        methodTitle: "* How the Card is Selected",
        methodText:
          "The weekly feature uses the number of weeks since a fixed Friday in the past as a seed. This method is stable and needs no database or server job. The update happens on Friday at nine in the morning Coordinated Universal Time and remains the same until the next Friday.",
        learnBasics: "Learn tarot basics",
        browseCards: "Browse all cards",
      };
}

/* =========================================================
   Component
========================================================= */
export default function FateFriday() {
  const items = useMemo(
    () => Object.values(cardData).sort((a, b) => a.slug.localeCompare(b.slug)),
    []
  );
  const thisWeek = useMemo(() => pickThisWeek(items), [items]);
  const pastWeeks = useMemo(() => getPastWeeks(items, 8), [items]);
  const lang = currentLanguage();
  const isSpanish = lang === "es";
  const copy = fateFridayCopy(isSpanish, thisWeek.card);

  // Long-form weekly note (cached per-week, 500-700 words)
  const [ff, setFf] = useState({ title: "", note: "" });
  const [ffLoading, setFfLoading] = useState(true);
  const [ffError, setFfError] = useState("");

  const ffPayload = useMemo(
    () => ({
      zodiac: "",
      theme: "reflection",
      tone: "gentle",
      cardName: copy.cardName || thisWeek.card?.name || "",
      weekAnchorUTC: thisWeek.anchor?.toISOString() || "",
    }),
    [copy.cardName, thisWeek.card?.name, thisWeek.anchor]
  );

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    // bump version to bust old short Spanish caches
    const cacheKey = `tf_ff_long_v5_${lang}_${thisWeek.key}_${thisWeek.card?.slug || ""}`;

    async function run() {
      try {
        setFfLoading(true);
        setFfError("");

        // localStorage cache first, only if long enough and language-safe
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (
            parsed?.note &&
            wc(parsed.note) >= 500 &&
            (!isSpanish || !ENGLISH_LEAK_RE.test(`${parsed.title || ""} ${parsed.note || ""}`))
          ) {
            if (!cancelled) {
              setFf(parsed);
              setFfLoading(false);
              return;
            }
          } else {
            // stale, short, or wrong-language cache: ignore
            localStorage.removeItem(cacheKey);
          }
        }

        const res = await fetch(`${API_BASE}/api/fatefriday`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(withLanguage(ffPayload)),
          signal: controller.signal,
          keepalive: true,
        });
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);

        const payload = {
          title: data?.title || copy.guideTitle,
          note: data?.note || [data?.intro, data?.guidance].filter(Boolean).join(" "),
        };

        if (isSpanish && (wc(payload.note) < 500 || ENGLISH_LEAK_RE.test(`${payload.title || ""} ${payload.note || ""}`))) {
          payload.title = copy.guideTitle;
          payload.note = spanishFateFridayNote(thisWeek.card);
        }

        if (!isSpanish && wc(payload.note) < 500) {
          // enforce length on client as a last resort
          throw new Error("Guide was shorter than expected. Please refresh.");
        }

        setFf(payload);
        try {
          localStorage.setItem(cacheKey, JSON.stringify(payload));
        } catch {}
      } catch (e) {
        if (e?.name === "AbortError" || cancelled) return;
        if (isSpanish) {
          setFf({ title: copy.guideTitle, note: spanishFateFridayNote(thisWeek.card) });
          setFfError("");
        } else {
          setFfError(copy.error);
        }
      } finally {
        if (!cancelled) setFfLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [copy.error, copy.guideTitle, ffPayload, isSpanish, lang, thisWeek.card, thisWeek.key, thisWeek.card?.slug]);

  const heroImg = thisWeek.card?.image || `/card/${thisWeek.card?.slug}.webp`;

  return (
    <div
      className={`${FONT} text-white min-h-screen relative overflow-hidden`}
      style={{ background: CANVAS_BG }}
    >
      <Seo
        title={copy.seoTitle}
        description={copy.description}
        url={isSpanish ? pageUrlEs : pageUrl}
        image={ogImage}
        schema={schemaGraph(thisWeek, pastWeeks, isSpanish)}
        keywords={pageKeywords}
        includeMega={true}
      />

      {heroImg ? (
        <Helmet>
          <link
            rel="preload"
            as="image"
            href={heroImg}
            imagesizes="(min-width: 768px) 300px, 60vw"
          />
        </Helmet>
      ) : null}

      {/* Ambient auras */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full opacity-25 blur-[160px] bg-[radial-gradient(circle,rgba(59,198,214,0.55)_0%,transparent_70%)]" />
        <div className="absolute -bottom-[25%] right-[5%] w-[700px] h-[700px] rounded-full opacity-20 blur-[140px] bg-[radial-gradient(circle,rgba(140,59,90,0.55)_0%,transparent_70%)]" />
      </div>

      {/* HERO */}
      <section className={`pt-6 ${WRAP} ${MAXW} text-center`}>
        <h1 className="text-xl sm:text-2xl tracking-wide font-bold">{copy.title}</h1>
        <div className="flex items-center justify-center mb-1 mt-1">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
          <span className="px-4 text-[#f3eaff]/100">*</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
        </div>
        <p className={`${TEXT} text-base mt-2`}>
          {copy.intro}
        </p>
        <div>
          <span className="text-[14px] text-white/70 italic mt-3">
            {copy.published}: {formatFriday(thisWeek.anchor, false, lang)}
          </span>
        </div>
      </section>

      {/* THIS WEEK */}
      <section id="this-week" className={`mt-4 ${WRAP} ${MAXW}`}>
        <div className={`${PANEL_BASE} ${SILK_TURQ} p-3 sm:p-6 md:p-6`}>
          <div className="relative z-[1]">
            <div className="grid md:grid-cols-[300px,1fr] gap-4 items-start">
              {/* Mobile title/subtitle ABOVE the card */}
              <div className="md:hidden text-center">
                <h2 className="text-lg font-semibold text-white/95">{copy.cardName}</h2>
                {copy.cardSubtitle ? (
                  <p className="text-sm italic text-white/80">{copy.cardSubtitle}</p>
                ) : null}
              </div>

              {/* Image */}
              <Link
                to={isSpanish ? localizedPath(cardPath(thisWeek.card)) : cardPath(thisWeek.card)}
                className="block group rounded-xl border border-white/0 bg-white/0 hover:bg-white/10 hover:border-white/25 transition"
              >
                <img
                  src={heroImg}
                  alt={isSpanish ? translateCardAlt(thisWeek.card.name) : thisWeek.card.name}
                  className="rounded-xl w-full h-auto object-contain max-h-[260px] sm:max-h-[320px] md:max-h-[520px]"
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "/icons/logo.png";
                  }}
                />
              </Link>

              {/* Copy */}
              <div>
                <div className="hidden md:block">
                  <h2 className="mt-2 text-lg font-semibold text-white/95">{copy.cardName}</h2>
                  {copy.cardSubtitle ? (
                    <p className="text-sm italic text-white/80">{copy.cardSubtitle}</p>
                  ) : null}
                </div>

                <p className={`${TEXT} text-[15px] mt-3`}>
                  {copy.cardIntro}
                </p>

                <div className="mt-4 flex flex-wrap gap-2">
                  <Link
                    to={isSpanish ? localizedPath(cardPath(thisWeek.card)) : cardPath(thisWeek.card)}
                    className="tf-outline-button"
                  >
                    {copy.openMeaning}
                  </Link>
                  <Link
                    to={isSpanish ? localizedPath(`/tarot/${suitSlug(thisWeek.card.suit)}`) : `/tarot/${suitSlug(thisWeek.card.suit)}`}
                    className="tf-outline-button"
                  >
                    {copy.exploreSuit}
                  </Link>
                </div>

                {/* Fate Friday weekly note (long-form) */}
                <div
                  className="mt-6 rounded-md border border-white/25 bg-white/10 p-3"
                  role="status"
                  aria-live="polite"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-white/95">
                      {ffLoading ? copy.guideTitle : ff.title || copy.guideTitle}
                    </h3>
                  </div>

                  {ffError ? (
                    <p className="mt-2 text-[15px] text-white/80">{ffError}</p>
                  ) : ffLoading ? (
                    <p className="mt-2 text-[15px] text-white/75">{copy.loading}</p>
                  ) : (
                    <p className="mt-2 text-sm text-white/85 whitespace-pre-line">{ff.note}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* top gleam */}
          <div className="absolute inset-x-0 top-0 h-px opacity-60 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
        </div>
      </section>

      {/* RECENT WEEKS */}
      <section id="recent-weeks" className={`mt-6 ${WRAP} ${MAXW}`}>
        <div className={`${PANEL_BASE} ${SILK_TURQ} p-3 sm:p-6 md:p-6`}>
          <div className="relative z-[1]">
            <h3 className="text-base sm:text-lg font-semibold text-white/95">{copy.recentTitle}</h3>
            <p className={`${TEXT} text-[15px] mt-2`}>
              {copy.recentIntro}
            </p>

            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
              {pastWeeks.map(({ card, anchor, key }) => (
                <Link
                  key={key}
                  to={isSpanish ? localizedPath(cardPath(card)) : cardPath(card)}
                  className={[
                    "transition-all duration-100 ease-out will-change-transform",
                    "group rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 hover:border-white/25 hover:-translate-y-1",
                  ].join(" ")}
                >
                  <div className="p-2 flex flex-col items-center gap-1">
                    <img
                      src={card.image || `/card/${card.slug}.webp`}
                      alt={isSpanish ? translateCardAlt(card.name) : card.name}
                      loading="lazy"
                      decoding="async"
                      className="h-[180px] w-full object-cover rounded-lg select-none pointer-events-none"
                      draggable={false}
                      onContextMenu={(e) => e.preventDefault()}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = "/icons/logo.png";
                      }}
                    />
                    <div className="text-center">
                      <h4 className="text-sm font-semibold text-white/95">{isSpanish ? translateCardName(card.name) : card.name}</h4>
                      <p className="text-xs text-white/75">{formatFriday(anchor, false, lang)}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
          <div className="absolute inset-x-0 top-0 h-px opacity-60 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
        </div>
      </section>

      {/* METHOD */}
      <section id="method" className={`mt-6 ${WRAP} ${MAXW} mb-12`}>
        <div className={`${PANEL_BASE} ${SILK_TURQ} p-3 sm:p-6 md:p-6`}>
          <div className="relative z-[1]">
            <h3 className="text-base sm:text-lg font-semibold text-white/95">{copy.methodTitle}</h3>
            <p className={`${TEXT} text-[15px] mt-2`}>
              {copy.methodText}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link
                to={isSpanish ? localizedPath("/what-is-tarot-cards-complete-guide") : "/what-is-tarot-cards-complete-guide"}
                className="tf-outline-button"
              >
                {copy.learnBasics}
              </Link>
              <Link
                to={isSpanish ? localizedPath("/tarot-cards") : "/tarot-cards"}
                className="tf-outline-button"
              >
                {copy.browseCards}
              </Link>
            </div>
          </div>
          <div className="absolute inset-x-0 top-0 h-px opacity-60 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
        </div>
      </section>
    </div>
  );
}

